'use client';
import { useStyle } from '@/classify';
import { Portal } from '@/components/Portal';
import { useCurrencySwitcher } from '@/hooks/Header/useCurrencySwitcher';
import useIsMobile from '@/hooks/use-is-mobile';
import { useMemo } from 'react';
import defaultClasses from './currencySwitcher.module.css';
import Shimmer from './currencySwitcher.shimmer';
import SwitcherItem from './switcherItem';

const CurrencySwitcher = props => {
  const {
    handleSwitchCountry,
    availableCountries,
    currentCountryCode,
    currencyMenuRef,
    currencyMenuTriggerRef,
    currencyMenuIsOpen,
    currencyMobileMenuIsOpen,
    handleTriggerClick,
  } = useCurrencySwitcher();

  const isMobile = useIsMobile();

  const classes = useStyle(defaultClasses, props.classes);

  const menuClassName = currencyMenuIsOpen ? classes.menu_open : classes.menu;

  const currentCountry = useMemo(() => {
    return availableCountries.find(temp => temp.code === currentCountryCode);
  }, [availableCountries, currentCountryCode]);

  if (!availableCountries) {
    return <Shimmer />;
  }

  if (availableCountries.length === 1) {
    return null;
  }

  const currencies = availableCountries.map(country => {
    return (
      <li
        role='option'
        aria-selected={currentCountryCode}
        key={country.code}
        className='cursor-pointer hover_bg-[#F8F3EF]'
      >
        <SwitcherItem
          active={country.code === currentCountryCode}
          onClick={handleSwitchCountry}
          option={country.code}
        >
          <img
            src={country.icon}
            className='h-auto w-7'
            alt=''
          />
          <span className='ml-[10px] text-[15px] leading-[20px] text-[#403732]'>{country.name}</span>
        </SwitcherItem>
      </li>
    );
  });

  const content = (
    <>
      <p className='mb-[5px] text-[15px] leading-[1.4] text-[#403732]'>Country</p>
      <ul
        className='mb-[10px]'
        role='listbox'
      >
        {currencies}
      </ul>
      <p className='mb-[5px] text-[15px] leading-[1.4] text-[#403732]'>Currency</p>
      <p className='mb-[10px] border border-[#E7DED5] px-[10px] text-[15px] leading-[40px]'>
        {currentCountry.currency}
      </p>

      <p className='mb-[5px] text-[15px] leading-[1.4] text-[#403732]'>Language</p>
      <p className='mb-[10px] border border-[#E7DED5] px-[10px] text-[15px] leading-[40px]'>
        {currentCountry.language}
      </p>
    </>
  );

  return (
    <div
      data-cy='CurrencySwitcher-root'
      className={classes.root}
    >
      <button
        type='button'
        data-cy='CurrencySwitcher-triggerButton'
        className={classes.trigger}
        aria-label={currentCountryCode}
        onClick={handleTriggerClick}
        ref={currencyMenuTriggerRef}
        aria-expanded={currencyMenuIsOpen}
      >
        <div className='flex h-[22px] w-[22px] items-center overflow-hidden rounded-full text-sm md_h-auto md_w-auto md_rounded-none'>
          <img
            alt=''
            className='h-auto w-[22px] lg_w-7'
            src={isMobile ? currentCountry.mobileIcon : currentCountry.icon}
          />
        </div>
        <svg
          className='hidden md_block'
          xmlns='http://www.w3.org/2000/svg'
          width='10'
          height='5'
          fill='none'
          viewBox='0 0 10 5'
        >
          <path
            fill='#5E5047'
            d='M10 0H7.93L5 2.93 2.07 0H0l5 5z'
          />
        </svg>
      </button>
      <div
        ref={currencyMenuRef}
        className={menuClassName}
      >
        {content}
      </div>
      {currencyMobileMenuIsOpen && isMobile ? (
        <Portal>
          <div className='fixed left-0 top-0 z-[50] h-full w-full bg-white px-[30px] pt-[60px] md_hidden'>
            <div
              className={classes.closeButton}
              onClick={handleTriggerClick}
            ></div>
            {content}
          </div>
        </Portal>
      ) : null}
    </div>
  );
};

export default CurrencySwitcher;
